import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraPersonPicker } from '@mono/ui-components/captains';
import { SpecraButton, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraInlineBanner, SpecraPopover } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { rfiQuestionStatus } from './questionStatus';
const NOTE_MAX = 500;
/**
 * "Send for review" (WRT-16): the reviewer comes from the RFI team (zou
 * rechecks that they can open the RFI), plus an optional note.
 */ export function SendForReviewPopover({ anchor, defaultReviewerId, error, isSending, onOpenChange, onSend, open, team }) {
    const [reviewer, setReviewer] = useState(defaultReviewerId);
    const [note, setNote] = useState('');
    return /*#__PURE__*/ _jsx(SpecraPopover, {
        anchor: anchor,
        "aria-label": "Send for review",
        onOpenChange: onOpenChange,
        open: open,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex w-80 flex-col gap-[var(--space-3)]",
            "data-testid": Testids.rfiWorkspace.reviewPopover,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.rfiWorkspace.reviewReviewer,
                    children: /*#__PURE__*/ _jsx(SpecraPersonPicker, {
                        label: "Reviewer",
                        onPersonChange: (p)=>setReviewer(p?.id ?? ''),
                        people: team.map((p)=>({
                                id: p.userId,
                                name: p.name || p.email,
                                email: p.email
                            })),
                        placeholder: "Choose a reviewer",
                        value: reviewer || undefined
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    htmlFor: "rfi-review-note",
                    label: "Note (optional)",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        "data-testid": Testids.rfiWorkspace.reviewNote,
                        id: "rfi-review-note",
                        maxLength: NOTE_MAX,
                        onChange: (e)=>setNote(e.target.value),
                        rows: 3,
                        value: note
                    })
                }),
                error ? /*#__PURE__*/ _jsx(SpecraText, {
                    role: "alert",
                    size: "meta",
                    tone: "danger",
                    children: error
                }) : null,
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.rfiWorkspace.reviewSend,
                    isLoading: isSending,
                    onClick: ()=>onSend({
                            reviewerUserId: reviewer,
                            note: note.trim()
                        }),
                    size: "sm",
                    children: "Send"
                })
            ]
        })
    });
}
/**
 * Reviewer actions on a question (WRT-16): Approve / Request changes while it
 * is in review, and the "Edited after it was approved" banner with Approve
 * again and Compare once the approved region changed.
 */ export function RfiAnswerReviewActions({ active, canApprove, compareHref, error, isApproving, isRequesting, onApprove, onRequestChanges }) {
    const [anchor, setAnchor] = useState(null);
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState('');
    const status = rfiQuestionStatus(active);
    const errorLine = error ? /*#__PURE__*/ _jsx(SpecraText, {
        role: "alert",
        size: "meta",
        tone: "danger",
        children: error
    }) : null;
    if (status === 'final_edited') {
        return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
            action: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-[var(--space-2)]",
                children: [
                    canApprove ? /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiWorkspace.approveAgain,
                        isLoading: isApproving,
                        onClick: onApprove,
                        size: "sm",
                        children: "Approve again"
                    }) : null,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        asChild: true,
                        size: "sm",
                        variant: "secondary",
                        children: /*#__PURE__*/ _jsx(Link, {
                            "data-testid": Testids.rfiWorkspace.compareApproved,
                            to: compareHref,
                            children: "Compare with the approved text"
                        })
                    })
                ]
            }),
            "data-testid": Testids.rfiWorkspace.finalEditedBanner,
            tone: "warning",
            children: [
                "Edited after it was approved.",
                errorLine
            ]
        });
    }
    if (status !== 'in_review' || !canApprove) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-[var(--space-2)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.rfiWorkspace.approve,
                isLoading: isApproving,
                onClick: onApprove,
                size: "sm",
                children: "Approve"
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.rfiWorkspace.requestChanges,
                onClick: ()=>setOpen((o)=>!o),
                ref: setAnchor,
                size: "sm",
                variant: "secondary",
                children: "Request changes"
            }),
            errorLine,
            /*#__PURE__*/ _jsx(SpecraPopover, {
                anchor: anchor,
                "aria-label": "Request changes",
                onOpenChange: setOpen,
                open: open,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex w-80 flex-col gap-[var(--space-3)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            htmlFor: "rfi-request-changes-note",
                            label: "What should change? (added as a comment)",
                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                "data-testid": Testids.rfiWorkspace.requestChangesNote,
                                id: "rfi-request-changes-note",
                                maxLength: NOTE_MAX,
                                onChange: (e)=>setNote(e.target.value),
                                rows: 3,
                                value: note
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.rfiWorkspace.requestChangesSend,
                            isLoading: isRequesting,
                            onClick: ()=>{
                                onRequestChanges(note.trim());
                                setOpen(false);
                                setNote('');
                            },
                            size: "sm",
                            children: "Send to the writer"
                        })
                    ]
                })
            })
        ]
    });
}
